import React, { useEffect, useState } from 'react';
import { ArrowRight, Eye, EyeOff, Loader2, Mail, Smartphone } from 'lucide-react';
import { accountService } from '../../services/accountService';
import './AuthForm.css';

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const PasswordInput = ({ value, onChange, placeholder, autoComplete, id }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="auth-password">
      <input
        id={id}
        name="password"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={100}
      />
      <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
};

const EmailInput = ({ value, onChange, autoFocus }) => (
  <label className="auth-field">
    <span>Email</span>
    <div className="auth-icon-input">
      <Mail size={16} />
      <input
        type="email"
        name="email"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="yourname@gmail.com"
        autoComplete="email"
        maxLength={120}
        autoFocus={autoFocus}
      />
    </div>
  </label>
);

const HEADINGS = {
  login: ['Log in', 'Welcome back! Log in with your email and password.'],
  register: ['Create your account', 'Just your mobile number, email and a password.'],
  forgot: ['Forgot password?', 'Enter your email — we’ll send you a 6-digit code to set a new password.'],
  reset: ['Set a new password', 'Enter the code from your email and choose a new password.']
};

/**
 * Log in (email + password) · Create account (mobile + email + password) · Forgot password (code by email)
 */
const AuthForm = ({ onSuccess, title, subtitle, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const go = next => {
    setMode(next);
    setError('');
    setInfo('');
    setPassword('');
    setCode('');
  };

  const run = async fn => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendCode = () => run(async () => {
    const res = await accountService.forgotPassword(email.trim());
    setMode('reset');
    setPassword('');
    setCode(res.devCode || '');
    setInfo(res.message);
    setResendIn(30);
  });

  const submit = e => {
    e.preventDefault();
    if (busy) return undefined;
    if (mode !== 'reset' && !isEmail(email)) return setError('Please enter a valid email address.');
    if (mode === 'login') {
      if (!password) return setError('Please enter your password.');
      return run(async () => onSuccess(await accountService.login(email.trim(), password)));
    }
    if (mode === 'register') {
      if (!/^[6-9]\d{9}$/.test(phone)) return setError('Please enter a valid 10-digit mobile number.');
      if (password.length < 6) return setError('Password must be at least 6 characters.');
      return run(async () => onSuccess(await accountService.register({ phone, email: email.trim(), password })));
    }
    if (mode === 'forgot') return sendCode();
    if (code.length !== 6) return setError('Please enter the 6-digit code from your email.');
    if (password.length < 6) return setError('New password must be at least 6 characters.');
    return run(async () => onSuccess(await accountService.resetPassword({ email: email.trim(), code, password })));
  };

  const [h, p] = mode === 'login' && title ? [title, subtitle || HEADINGS.login[1]] : HEADINGS[mode];

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      {(mode === 'login' || mode === 'register') && (
        <div className="auth-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'active' : ''} onClick={() => go('login')}>Log in</button>
          <button type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'active' : ''} onClick={() => go('register')}>Create account</button>
        </div>
      )}

      <h2 className="auth-title">{h}</h2>
      <p className="auth-subtitle">{p}</p>

      {mode === 'register' && (
        <label className="auth-field">
          <span>Mobile number</span>
          <div className="auth-phone-input">
            <span className="auth-prefix">+91</span>
            <input
              type="tel"
              name="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="98765 43210"
              value={phone}
              onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(''); }}
            />
          </div>
        </label>
      )}

      {mode === 'reset' ? (
        <div className="auth-phone-chip">
          <Mail size={15} /> {email.trim()}
          <button type="button" onClick={() => go('forgot')}>Change</button>
        </div>
      ) : (
        <EmailInput value={email} onChange={v => { setEmail(v); setError(''); }} autoFocus={mode !== 'register'} />
      )}

      {mode === 'reset' && (
        <label className="auth-field">
          <span>6-digit code from your email</span>
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            placeholder="• • • • • •"
            autoComplete="one-time-code"
            className="auth-code"
            autoFocus
          />
        </label>
      )}

      {mode !== 'forgot' && (
        <label className="auth-field">
          <span>{mode === 'login' ? 'Password' : mode === 'reset' ? 'New password' : 'Create a password'}</span>
          <PasswordInput
            id={`auth-password-${mode}`}
            value={password}
            onChange={v => { setPassword(v); setError(''); }}
            placeholder={mode === 'login' ? 'Your password' : 'At least 6 characters'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </label>
      )}

      {mode === 'login' && (
        <button type="button" className="auth-link small right" onClick={() => go('forgot')}>Forgot password?</button>
      )}

      {info && <p className="auth-info">{info}</p>}
      {error && <p className="auth-error" role="alert">{error}</p>}

      <button type="submit" className="auth-submit" disabled={busy}>
        {busy ? <Loader2 size={17} className="auth-spin" /> : (
          <>
            {{ login: 'Log in', register: 'Create account', forgot: 'Send code to my email', reset: 'Save password & log in' }[mode]}
            <ArrowRight size={16} />
          </>
        )}
      </button>

      {mode === 'reset' && (
        <button type="button" className="auth-link" onClick={sendCode} disabled={busy || resendIn > 0}>
          {resendIn > 0 ? `Send the code again in ${resendIn}s` : 'Send the code again'}
        </button>
      )}
      {(mode === 'forgot' || mode === 'reset') && (
        <button type="button" className="auth-link" onClick={() => go('login')}>Back to log in</button>
      )}
      {mode === 'register' && (
        <p className="auth-note"><Smartphone size={12} /> Your number is only used for delivery and order updates.</p>
      )}
    </form>
  );
};

export default AuthForm;
